import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import marked from 'marked';
import { IForecastGuidanceDefaultState } from './DefaultState';
import moment from 'moment-timezone';
import { EditOutlined } from '@ant-design/icons';
import FormMarkDownEditor from '../common/FormGroup/FormMarkDownEditor';
import { Modal, Button } from 'antd';

const ForecastGuidancePreviewWrapperStyle = styled.div<IForecastGuidancePreviewWrapperStyle>`
    padding: 1px;
    z-index: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;
    > div {
        height: 100%;
        padding: ${(props: IForecastGuidancePreviewWrapperStyle) => props.padding}px;
        border: ${(props: IForecastGuidancePreviewWrapperStyle) => props.border};
    }
`;
const ForecastGuidanceHeaderStyle = styled.div<IForecastGuidanceHeaderStyle>`
    border-bottom: ${(props) => props.border};
`;
const ForecastGuidanceTitleStyle = styled.h2<IStyleForecastGuidanceProps>`
    font-size: ${(props: IStyleForecastGuidanceProps) => props.fontSize * 1.5}%;
    text-align: left;
    font-weight: 700;
`;
const ForecastGuidanceContentStyle = styled.div<IStyleForecastGuidanceProps>`
    font-size: ${(props) => props.fontSize}%;
    color: #333;
    text-align: left;
    padding-top: 10px;

    h1, h2, h3 {
        font-weight: 700;
        line-height: 1.5;
    }
    h1{ 
        font-size: ${(props) => props.fontSize * 2}%
    }
    h2{
        font-size: ${(props) => props.fontSize * 1.8}%
    }
    h3{
        font-size ${(props) => props.fontSize * 1.6}%
    }
    h4{ 
        font-size ${(props) => props.fontSize * 1.4}%
    }
    h5{ 
        font-size ${(props) => props.fontSize * 1.3}%
    }
    p{
        font-size: ${(props) => props.fontSize}%;
        line-height: 1.5;
        white-space: pre-line;
    }
    p:last-child {
        margin-bottom: 0;
    }
    blockquote {  
        background: #f9f9f9;
        border-left: 10px solid #ccc;
        margin: 1.5em 10px;
        padding: 1em 10px;
        p {
            margin-bottom: 0;
            white-space: pre-line;
        }
    }
    img {
        max-width: 90%;
        width: auto;
        display: block;
    }
`;

const ForecastGuidanceLastUpdatedStyle = styled.div<IStyleForecastGuidanceProps>`
    font-size: ${(props) => props.fontSize}%;
    color: #666;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
    text-align: left;
`;

const ForecastGuidanceEditor = styled.div`
    display: flex;
    flex-direction: row;
`;

export interface IStyleForecastGuidanceProps {
    fontSize: number;
}
export interface IForecastGuidanceHeaderStyle {
    border: string;
}
export interface IForecastGuidancePreviewWrapperStyle {
    border: string;
    padding: number;
}
export interface IForecastGuidancePreview {
    staticBlockState: IForecastGuidanceDefaultState;
    timezone: string;
    onEditMarkdown?: (content: string) => Promise<void>;
}

interface RendererProps {
    title: string;
    content: string;
    fontSize: number;
    timezone: string;
    showLastUpdated: boolean;
    lastUpdated: string;
}

const Renderer = ({ title, content, timezone, fontSize, lastUpdated, showLastUpdated }: RendererProps) => {
    const lastUpdatedTimezone = moment(new Date()).tz(timezone);
    const lastUpdatedFormatted = `${lastUpdatedTimezone.format('llll')} ${lastUpdatedTimezone.format('z')}`;
    const showHeaderBorderBottom = (showLastUpdated || title !== null) && (showLastUpdated || title !== '');

    return (
        <div>
            {showHeaderBorderBottom && (
                <ForecastGuidanceHeaderStyle border={showHeaderBorderBottom ? '1px solid #EEE' : 'none'}>
                    <ForecastGuidanceTitleStyle fontSize={fontSize}>{title}</ForecastGuidanceTitleStyle>
                    {showLastUpdated && lastUpdated && (
                        <ForecastGuidanceLastUpdatedStyle fontSize={fontSize}>Last updated {lastUpdatedFormatted}</ForecastGuidanceLastUpdatedStyle>
                    )}
                </ForecastGuidanceHeaderStyle>
            )}

            <ForecastGuidanceContentStyle fontSize={fontSize} dangerouslySetInnerHTML={{ __html: marked(content) }}></ForecastGuidanceContentStyle>
        </div>
    );
};

const ForecastGuidancePreview = ({ staticBlockState, timezone, onEditMarkdown }: IForecastGuidancePreview) => {
    const { settings, content, title, lastUpdated } = staticBlockState;
    const { fontSize, showLastUpdated, showBoxAround } = settings;
    const [isHovering, setIsHovering] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [newContent, setNewContent] = useState(content || '');
    const [isUpdating, setIsUpdating] = useState(false);
    const [containsIllegalCharacters, setContainsIllegalCharacters] = useState(false);

    const forecastGuidanceWrapperBorder = showBoxAround ? '2px solid rgba(128, 128, 128, 1)' : 'none';
    const forecastGuidanceWrapperPadding = showBoxAround ? 15 : 0;

    useEffect(() => {
        setContainsIllegalCharacters(!!newContent.match(/([<][^>]*[>])/));
    }, [newContent]);

    return (
        <ForecastGuidancePreviewWrapperStyle
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            border={forecastGuidanceWrapperBorder}
            padding={forecastGuidanceWrapperPadding}
        >
            <div>
                {onEditMarkdown && (
                    <Modal
                        title='Provide your guidance'
                        visible={isEditing}
                        onOk={async () => {
                            setIsUpdating(true);
                            await onEditMarkdown(newContent);
                            setIsEditing(false);
                            setIsUpdating(false);
                        }}
                        okButtonProps={{
                            disabled: containsIllegalCharacters,
                        }}
                        onCancel={() => {
                            setIsEditing(false);
                            setNewContent(content || '');
                        }}
                        width={1000}
                        confirmLoading={isUpdating}
                        cancelButtonProps={{
                            disabled: isUpdating,
                        }}
                    >
                        <ForecastGuidanceEditor>
                            <div style={{ flex: 1 }}>
                                <FormMarkDownEditor value={newContent} onChange={setNewContent} />
                                {containsIllegalCharacters && (
                                    <div style={{ color: 'rgb(205,114,121)' }}>
                                        We cannot support XML format in the text.
                                        <br />
                                        Please make sure that there are enclosing angle brackets {'(< >)'}.
                                        <br />
                                        Apologies, this is for security reasons.
                                        <br />
                                    </div>
                                )}
                            </div>
                            <div style={{ width: 20 }} />
                            <div style={{ flex: 1, border: '1px solid #16161d', padding: 10, borderRadius: 5 }}>
                                <Renderer
                                    title={title}
                                    content={newContent || ''}
                                    fontSize={fontSize}
                                    showLastUpdated={showLastUpdated}
                                    timezone={timezone}
                                    lastUpdated={lastUpdated}
                                />
                            </div>
                        </ForecastGuidanceEditor>
                    </Modal>
                )}
                {!isEditing && (
                    <div>
                        {onEditMarkdown && isHovering && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                }}
                            >
                                <Button onClick={() => setIsEditing(true)} icon={<EditOutlined />} type='primary' size='large' shape='round'>
                                    Edit
                                </Button>
                            </div>
                        )}
                        <Renderer
                            title={title}
                            content={content || ''}
                            fontSize={fontSize}
                            showLastUpdated={showLastUpdated}
                            timezone={timezone}
                            lastUpdated={lastUpdated}
                        />
                    </div>
                )}
            </div>
        </ForecastGuidancePreviewWrapperStyle>
    );
};

export default ForecastGuidancePreview;
